import { ReactNode, useState } from 'react';
import { Header } from '../Header/Header';
import { Sidebar } from '../Sidebar/Sidebar';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Toaster } from '@/components/ui/toaster';
import { AIAssistantProvider } from '@/components/ai-assistant';

const SIDEBAR_STATE_KEY = 'sentra-sidebar-open';

interface LayoutProps {
  children: ReactNode;
  fullWidth?: boolean;
  /** Fill the viewport instead of scrolling the page — for full-height views like the AI chat. */
  fill?: boolean;
}

function getSavedSidebarState(): boolean {
  const stored = localStorage.getItem(SIDEBAR_STATE_KEY);
  return stored !== 'false'; // default open
}

export function Layout({ children, fullWidth = false, fill = false }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(getSavedSidebarState);

  const handleSidebarChange = (open: boolean) => {
    setSidebarOpen(open);
    localStorage.setItem(SIDEBAR_STATE_KEY, String(open));
  };

  // One top strip on every page: sidebar trigger left, header cluster right.
  // (Pages used to be shrunk with transform: scale() below 1440px, which
  // blurred text and mispositioned every popover/dropdown.)
  const topStrip = (
    <div className="flex h-14 shrink-0 items-center gap-2">
      <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
      <Header inline />
    </div>
  );

  return (
    <SidebarProvider defaultOpen={sidebarOpen} onOpenChange={handleSidebarChange}>
      <div className={`flex ${fill ? 'h-screen' : 'min-h-screen'} w-full overflow-x-hidden bg-background`}>
        <Sidebar />
        <div className="flex flex-col flex-1 min-w-0 relative">
          {fill ? (
            <main className="flex-1 min-h-0 flex flex-col overflow-hidden w-full px-4 pb-4">
              {topStrip}
              <div className="flex-1 min-h-0 flex flex-col max-w-[1800px] mx-auto w-full">
                {children}
              </div>
            </main>
          ) : (
            <main className="flex-1 w-full overflow-auto px-4 lg:px-6">
              {topStrip}
              <div
                className={`${fullWidth ? 'max-w-[1800px]' : 'max-w-7xl'} mx-auto w-full pt-2 pb-24 lg:px-2`}
              >
                {children}
              </div>
            </main>
          )}
        </div>
      </div>
      <Toaster />
      <AIAssistantProvider>
        {/* AI Assistant floating button and panel */}
        <></>
      </AIAssistantProvider>
    </SidebarProvider>
  );
}
